<template>
    <div v-if="prescription" class="lib-row" :class="{ readonly }"
        :style="!!$listeners.click ? 'cursor: pointer' : null"
        @click="$emit('click')" @mousedown="$emit('mousedown', prescription, $event)">
        <div v-if="showArrows" class="lib-arrow" @click.stop="$emit('click:prev')">
            <v-icon size="20">mdi-chevron-left</v-icon>
        </div>
        <div class="lib-icon">
            <v-icon size="20" color="accent">mdi-clipboard-list-outline</v-icon>
        </div>
        <div class="lib-info">
            <div class="lib-name text-truncate">{{ prescription.name }}</div>
            <div class="lib-meta text-truncate">
                {{ prescription.config?.sets?.length || 0 }}
                <span class="text-lowercase">{{ $tc('exercises.sets', 2) }}</span>
                <template v-if="!noPauseText"> &middot; {{ $t('exercises.pause') }}: {{ prescriptionTime }}</template>
            </div>
        </div>
        <div v-if="badge" class="lib-badge">{{ badge }}</div>
        <v-menu offset-y v-if="withMenu">
            <template v-slot:activator="{ on, attrs }">
                <button type="button" class="lib-more" v-bind="attrs" v-on="on" @click.stop @mousedown.stop>
                    <v-icon size="18">mdi-dots-vertical</v-icon>
                </button>
            </template>
            <v-list dense>
                <v-list-item link @click="$emit('click:edit')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-pencil-outline</v-icon>
                    <v-list-item-title>{{ $t('common.edit_prescription') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="$emit('click:copy')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                    <v-list-item-title>{{ $t('common.copy_prescription') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="sharePrescription" v-if="canShare">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-share-variant</v-icon>
                    <v-list-item-title>{{ $t('common.share_prescription') }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item link @click="$emit('click:delete')">
                    <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                    <v-list-item-title class="error--text">{{ $t('common.delete_prescription') }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
        <div v-if="showArrows" class="lib-arrow" @click.stop="$emit('click:next')">
            <v-icon size="20">mdi-chevron-right</v-icon>
        </div>
    </div>
</template>

<script>

export default {
    props: {
        prescription: {
            type: Object,
            default: null,
        },
        withMenu: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        showArrows: {
            type: Boolean,
            default: false
        },
        noPauseText: {
            type: Boolean,
            default: false
        },
        canShare: {
            type: Boolean,
            default: false
        },
        badge: {
            type: [Number, String],
            default: null,
        },
    },
    computed: {
        prescriptionImg(vm = this) {
            return vm.prescription.img || vm.imgPlaceholder
        },
        prescriptionTime(vm = this) {
            return !vm.prescription.hasTime ? vm.$t('common.not_available_short') : vm.prescription.time
        },
        isLgScreen(vm = this) {
            return vm.$vuetify.breakpoint.lgAndUp
        },
        contentCols(vm = this) {
            if (vm.showArrows) return vm.isLgScreen ? 8 : 7
            return 9
        },
    },
    methods: {
        sharePrescription() {
            const vm = this
            vm.$emit('click:share')
        },
    }
}
</script>

<style scoped>
.lib-row {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all .15s;
}

.lib-row:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface2-base);
}

.lib-row.readonly {
    pointer-events: none;
}

.lib-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.lib-info {
    flex: 1;
    min-width: 0;
}

.lib-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.lib-meta {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    margin-top: 2px;
}

.lib-more {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    transition: all .15s;
}

.lib-more:hover {
    color: var(--v-txtSecondary-base);
    border-color: var(--v-outline2-base);
}

.lib-more .v-icon {
    color: inherit !important;
}

.lib-badge {
    width: 20px;
    height: 20px;
    min-width: 20px;
    border-radius: 50%;
    background: var(--v-primary-base);
    color: #0C0E12;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.lib-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.lib-arrow:hover {
    color: var(--v-txtPrimary-base);
}
</style>